import { useRef, useState, useEffect } from 'react';
import { Store, CreditCard, Activity, Star } from 'lucide-react';
import { GOOGLE_REVIEWS } from '@/lib/googleReviewsData';
import { useLanguage } from '@/lib/LanguageContext';
import ClientLogo from './ClientLogo';
import { CLIENT_LOGOS } from '@/lib/clientLogos';


function useReveal(ref) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([e]) => {if (e.isIntersecting) setRevealed(true);}, { threshold: 0.05 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return revealed;
}

export default function TrustSection() {
  const ref = useRef(null);
  const revealed = useReveal(ref);
  const { t } = useLanguage();

  const stats = [
    { value: '+500', label: t('trust.stat1'), color: '#0095fc', icon: Store },
    { value: '€500M+', label: t('trust.stat2'), color: '#7800f7', icon: CreditCard },
    { value: '99.9%', label: t('trust.stat3'), color: '#0ff0b3', icon: Activity },
    { value: `${GOOGLE_REVIEWS.rating.toFixed(1)}★`, label: t('trust.stat4'), color: '#0095fc', icon: Star },
  ];

  return (
    <section id="casos-exito" ref={ref} className="py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-xs font-black tracking-widest uppercase text-blue-500 mb-4 px-3 py-1 bg-blue-50 rounded-full">{t('trust.eyebrow')}</span>
          <h1 className="font-heading font-light text-4xl lg:text-5xl text-gray-900 leading-tight mb-4 tracking-tight">
            {t('trust.title1')} <span className="text-brand-blue">{t('trust.titleHighlight')}</span> {t('trust.titleEnd')}
          </h1>
          <p className="text-gray-500 text-lg font-light">{t('trust.desc')}</p>
        </div>

        {/* Stats bar */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 transition-all duration-700 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {stats.map((s) =>
          <div key={s.label} className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
              <s.icon size={26} className="mx-auto mb-3" style={{ color: s.color }} strokeWidth={2} />
              <p className="font-black text-3xl lg:text-4xl mb-2" style={{ color: s.color }}>{s.value}</p>
              <p className="text-gray-500 text-sm font-medium">{s.label}</p>
            </div>
          )}
        </div>

        {/* Logo strip */}
        <div className={`transition-all duration-700 delay-500 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-center text-gray-400 text-xs font-bold uppercase tracking-widest mb-6">{t('trust.logoStrip')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
            {CLIENT_LOGOS.map((client) =>
            <div key={client.name} className="flex flex-col items-center justify-center gap-1.5 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all p-4 h-24">
                <ClientLogo domain={client.domain} name={client.name} />
                <span className="font-heading font-semibold text-gray-500 text-xs text-center leading-tight">{client.name}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);
}